import { useState } from "react";
import { object, string, mixed } from "yup";
import { Banner } from "components/DesignSystem/Banner/Banner";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useToast } from "hooks/useToast";
import {
  useGetTaxBenefitsQuery,
  useUploadTaxReturnMutation,
} from "store/apis/estimatedTax";
import {
  RndConflictResolution,
  UploadTaxReturnConflict,
  UploadTaxReturnSavedResult,
} from "types/Models/estimatedTax";
import { currency } from "utils/Currency";
import { hasUploadedReturnForYear } from "util/hasUploadedReturnForYear";
import {
  HTTP_STATUS_PAYLOAD_TOO_LARGE,
  TAX_RETURN_FILE_TOO_LARGE_ERROR,
  RND_KEPT_STATUS_LABEL,
  RND_STATUS_CONFLICT,
  RND_STATUS_POPULATED,
  TAX_RETURN_UPLOAD_ERROR_FALLBACK,
  UPLOAD_RETURN_STEP_CONFIRM_REPLACE,
  UPLOAD_RETURN_STEP_RESOLVE_CONFLICT,
  UPLOAD_RETURN_STEP_UPLOAD,
} from "./constants";
import { RndConflictChoice } from "./RndConflictChoice";
import {
  UploadTaxReturnFields,
  UploadTaxReturnValues,
} from "./UploadTaxReturnFields";

type Props = {
  onClose: () => void;
  entityId: string;
  defaultTaxYear: number;
};

type UploadReturnStep =
  | { type: typeof UPLOAD_RETURN_STEP_UPLOAD }
  | { type: typeof UPLOAD_RETURN_STEP_CONFIRM_REPLACE }
  | {
      type: typeof UPLOAD_RETURN_STEP_RESOLVE_CONFLICT;
      conflictResult: UploadTaxReturnConflict;
    };

const UPLOAD_STEP: UploadReturnStep = { type: UPLOAD_RETURN_STEP_UPLOAD };
const CONFIRM_REPLACE_STEP: UploadReturnStep = {
  type: UPLOAD_RETURN_STEP_CONFIRM_REPLACE,
};

const validationSchema = object({
  tax_year: string().required("Tax year is required"),
  file: mixed().required("A tax return file is required"),
});

type UploadReturnError = {
  status?: number | string;
  originalStatus?: number;
  data?: { error?: string | { message?: string } };
};

const getUploadErrorMessage = ({
  status,
  originalStatus,
  data,
}: UploadReturnError): string => {
  // nginx rejects oversized uploads with an HTML 413, so RTK reports a
  // PARSING_ERROR and the real status only survives as originalStatus.
  if (
    status === HTTP_STATUS_PAYLOAD_TOO_LARGE ||
    originalStatus === HTTP_STATUS_PAYLOAD_TOO_LARGE
  ) {
    return TAX_RETURN_FILE_TOO_LARGE_ERROR;
  }

  const backendError = data?.error;

  if (typeof backendError === "string") return backendError;

  return backendError?.message ?? TAX_RETURN_UPLOAD_ERROR_FALLBACK;
};

const getSuccessMessage = (result: UploadTaxReturnSavedResult): string => {
  if (result.rnd_status !== RND_STATUS_POPULATED) {
    return `Kept your ${result.tax_year} R&D amount from ${
      RND_KEPT_STATUS_LABEL[result.rnd_status]
    }. Nothing was added from the return.`;
  }

  return `Added ${currency({
    amount: result.extracted_amount,
    maximumFractionDigits: 0,
  })} of R&D (§174) from your ${result.tax_year} return`;
};

export const UploadTaxReturnModal = ({
  onClose,
  entityId,
  defaultTaxYear,
}: Props) => {
  const { successToast } = useToast();
  const { data: taxBenefits } = useGetTaxBenefitsQuery({ entityId });
  const [uploadTaxReturn, { isLoading }] = useUploadTaxReturnMutation();

  const [step, setStep] = useState<UploadReturnStep>(UPLOAD_STEP);
  const [errorMessage, setErrorMessage] = useState("");
  const [pendingResolution, setPendingResolution] =
    useState<RndConflictResolution | null>(null);

  const initialValues: UploadTaxReturnValues = {
    tax_year: String(defaultTaxYear),
    file: null,
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !isLoading) onClose();
  };

  const handleBack = () => setStep(UPLOAD_STEP);

  const handleClearError = () => setErrorMessage("");

  const uploadReturn = async (
    { tax_year, file }: UploadTaxReturnValues,
    resolution?: RndConflictResolution
  ) => {
    if (!file || isLoading) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("tax_year", tax_year);
    if (resolution) formData.append("rnd_conflict_resolution", resolution);

    setErrorMessage("");
    try {
      const result = await uploadTaxReturn({ entityId, formData }).unwrap();

      if (result.rnd_status === RND_STATUS_CONFLICT) {
        setPendingResolution(null);
        setStep({
          type: UPLOAD_RETURN_STEP_RESOLVE_CONFLICT,
          conflictResult: result,
        });
        return;
      }

      successToast({ message: getSuccessMessage(result) });
      onClose();
    } catch (error) {
      setPendingResolution(null);
      setErrorMessage(getUploadErrorMessage(error as UploadReturnError));
      setStep(UPLOAD_STEP);
    }
  };

  const handleSubmit = async (values: UploadTaxReturnValues) => {
    const shouldConfirmReplace =
      step.type === UPLOAD_RETURN_STEP_UPLOAD &&
      hasUploadedReturnForYear(taxBenefits, Number(values.tax_year));

    if (shouldConfirmReplace) {
      setStep(CONFIRM_REPLACE_STEP);
      return;
    }

    await uploadReturn(values);
  };

  const handleResolve = (
    values: UploadTaxReturnValues,
    resolution: RndConflictResolution
  ) => {
    setPendingResolution(resolution);
    void uploadReturn(values, resolution);
  };

  const renderStep = (values: UploadTaxReturnValues) => {
    if (step.type === UPLOAD_RETURN_STEP_RESOLVE_CONFLICT) {
      return (
        <>
          <Modal.Body>
            <RndConflictChoice
              conflictResult={step.conflictResult}
              pendingResolution={pendingResolution}
              onResolve={(resolution) => handleResolve(values, resolution)}
            />
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button type="button" disabled={isLoading} onClick={onClose}>
              Cancel
            </Button>
          </Modal.FooterButtonGroup>
        </>
      );
    }

    if (step.type === UPLOAD_RETURN_STEP_CONFIRM_REPLACE) {
      return (
        <>
          <Modal.Body>
            <ReplaceReturnWarning taxYear={values.tax_year} />
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button type="button" disabled={isLoading} onClick={handleBack}>
              Back
            </Button>
            <Button
              type="submit"
              customType="danger"
              isLoading={isLoading}
              disabled={isLoading}
            >
              Replace
            </Button>
          </Modal.FooterButtonGroup>
        </>
      );
    }

    return (
      <>
        <Modal.Body className="t-space-y-3">
          <UploadTaxReturnFields
            latestTaxYear={defaultTaxYear}
            isUploading={isLoading}
            errorMessage={errorMessage}
            onChange={handleClearError}
          />
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button type="button" disabled={isLoading} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            customType="primary"
            isLoading={isLoading}
            disabled={!values.file || !values.tax_year || isLoading}
          >
            Upload
          </Button>
        </Modal.FooterButtonGroup>
      </>
    );
  };

  return (
    <Modal.Root open onOpenChange={handleOpenChange}>
      <Modal.Content useCustomOverlay>
        <FormikForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <>
              <Modal.Header>
                <Modal.Title>Upload return</Modal.Title>
                <Modal.Close disabled={isLoading} />
              </Modal.Header>
              {renderStep(values)}
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};

const ReplaceReturnWarning = ({ taxYear }: { taxYear: string }) => (
  <Banner
    type="warning"
    description={`This will replace the R&D amount from the previously uploaded ${taxYear} return. Do you want to continue?`}
  />
);
